import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '@/contexts/AuthContext';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Progress } from '@/components/ui/progress';
import { ArrowLeft, Search, MoreHorizontal, ChevronLeft, ChevronRight, Phone, UserPlus, Upload, Zap, RefreshCw, Download, Filter, X, Users, Target, Calendar, Store, MapPin, Check } from 'lucide-react';
import ProspectDrawer from '@/components/ProspectDrawer';
import ExportModal from '@/components/ExportModal';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { useDropzone } from 'react-dropzone';

const STATUS_LABELS = {
  en_attente: 'En attente', trouve: 'Trouvé', a_verifier: 'À vérifier',
  non_trouve: 'Non trouvé', ignore: 'Ignoré', valide_manuellement: 'Validé'
};
const COMMERCIAL_LABELS = {
  nouveau: 'Nouveau', a_appeler: 'À appeler', appele: 'Appelé', rdv_obtenu: 'RDV obtenu',
  en_negociation: 'En négociation', refus: 'Refus', rappel_demande: 'Rappel', injoignable: 'Injoignable',
  mauvais_numero: 'Mauvais n°', converti: 'Converti',
  mail_prospection_envoye: 'Mail Prospection envoyé', mail_doc_envoye: 'Mail Doc envoyé',
};

export const formatCA = (val) => {
  const n = parseInt(val, 10);
  if (!n || isNaN(n) || n <= 0) return '';
  if (n >= 1_000_000) {
    const m = n / 1_000_000;
    return `${m % 1 === 0 ? m.toFixed(0) : m.toFixed(1).replace('.', ',')} M€`;
  }
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)} K€`;
  return `${n} €`;
};

export default function CampaignDetailPage() {
  const { id: campaignId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [campaign, setCampaign] = useState(null);
  const [prospects, setProspects] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage] = useState(30);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ statut_recherche: '', statut_commercial: '', enseigne: [], departement: [] });
  const [loading, setLoading] = useState(true);
  const [selectedProspect, setSelectedProspect] = useState(null);
  const [showExport, setShowExport] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [commerciaux, setCommerciaux] = useState([]);
  const [imports, setImports] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [enriching, setEnriching] = useState({});
  const [facets, setFacets] = useState({ enseignes: [], departements: [] });

  const loadCampaign = useCallback(async () => {
    try {
      const { data } = await api.get(`/campaigns/${campaignId}`);
      setCampaign(data);
    } catch { navigate('/campaigns'); }
  }, [campaignId, navigate]);

  const loadProspects = useCallback(async () => {
    const params = { page, per_page: perPage, campagne_id: campaignId, sort_by: 'score_confiance', sort_order: 'desc' };
    if (search) params.search = search;
    if (filters.statut_recherche) params.statut_recherche = filters.statut_recherche;
    if (filters.statut_commercial) params.statut_commercial = filters.statut_commercial;
    if (filters.enseigne && filters.enseigne.length) params.enseigne = filters.enseigne.join(',');
    if (filters.departement && filters.departement.length) params.departement = filters.departement.join(',');
    try {
      const { data } = await api.get('/prospects', { params });
      setProspects(data.prospects);
      setTotal(data.total);
    } catch {}
    setLoading(false);
  }, [page, perPage, search, filters, campaignId]);

  const loadFacets = useCallback(async () => {
    try {
      const { data } = await api.get('/prospects/filters/facets', { params: { campagne_id: campaignId } });
      setFacets(data);
    } catch {}
  }, [campaignId]);

  const loadImports = useCallback(async () => {
    try {
      const { data } = await api.get('/imports');
      setImports(data.filter(i => i.campagne_id === campaignId));
    } catch {}
  }, [campaignId]);

  useEffect(() => { loadCampaign(); loadProspects(); loadImports(); loadFacets(); }, [loadCampaign, loadProspects, loadImports, loadFacets]);

  const toggleEnseigne = (e) => {
    setFilters(f => ({ ...f, enseigne: f.enseigne.includes(e) ? f.enseigne.filter(x => x !== e) : [...f.enseigne, e] }));
    setPage(1);
  };
  const toggleDept = (d) => {
    setFilters(f => ({ ...f, departement: f.departement.includes(d) ? f.departement.filter(x => x !== d) : [...f.departement, d] }));
    setPage(1);
  };
  useEffect(() => { api.get('/users/commerciaux').then(r => setCommerciaux(r.data)).catch(() => {}); }, []);

  // Poll enrichment
  useEffect(() => {
    const ids = Object.keys(enriching).filter(k => enriching[k]);
    if (!ids.length) return;
    const interval = setInterval(async () => {
      for (const iid of ids) {
        try {
          const { data } = await api.get(`/imports/${iid}`);
          setImports(prev => prev.map(i => i.id === iid ? { ...i, ...data } : i));
          if (data.statut === 'termine' || data.statut === 'erreur') {
            setEnriching(prev => ({ ...prev, [iid]: false }));
            toast.success('Enrichissement terminé');
            loadProspects();
            loadCampaign();
          }
        } catch {}
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [enriching, loadProspects, loadCampaign]);

  const onDrop = useCallback(async (files) => {
    const file = files[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const { data } = await api.post(`/imports/upload?campagne_id=${campaignId}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const msgs = [`${data.nb_prospects} prospects ajoutés`];
      if (data.nb_doublons > 0) msgs.push(`${data.nb_doublons} doublons`);
      if (data.nb_wasabi_exclus > 0) msgs.push(`🛡 ${data.nb_wasabi_exclus} clients Wasabi exclus`);
      toast.success(`Import: ${msgs.join(' • ')}`);
      loadImports(); loadProspects(); loadCampaign();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur d'import"); }
    setUploading(false);
  }, [campaignId, loadImports, loadProspects, loadCampaign]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop, accept: { 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'] }, maxFiles: 1
  });

  const startEnrichment = async (importId, force = false) => {
    try {
      await api.post(`/imports/${importId}/enrich?force=${force}`);
      setEnriching(prev => ({ ...prev, [importId]: true }));
      toast.success('Enrichissement démarré');
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const startDirigeantEnrichment = async (importId) => {
    try {
      await api.post(`/imports/${importId}/enrich-dirigeants`);
      setEnriching(prev => ({ ...prev, [importId]: true }));
      toast.success('Recherche des dirigeants démarrée');
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const quickCallResult = async (prospectId, resultat) => {
    try {
      await api.post(`/prospects/${prospectId}/call-result`, { resultat });
      toast.success('Statut mis à jour');
      loadProspects(); loadCampaign();
    } catch { toast.error('Erreur'); }
  };

  const reSearchProspect = async (prospectId) => {
    try {
      toast.info('Recherche en cours...');
      await api.post(`/prospects/${prospectId}/re-search`);
      toast.success('Recherche terminée');
      loadProspects();
    } catch { toast.error('Erreur de recherche'); }
  };

  const totalPages = Math.ceil(total / perPage);
  if (!campaign) return <div className="animate-pulse-subtle text-center py-20 text-[#71717A]">Chargement...</div>;

  const enrichRate = campaign.nb_prospects > 0 ? Math.round((campaign.nb_enrichis / campaign.nb_prospects) * 100) : 0;

  return (
    <div className="space-y-5 animate-fade-in" data-testid="campaign-detail-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/campaigns')} className="h-8 w-8 p-0">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-2.5">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: campaign.couleur }} />
            <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>{campaign.nom}</h1>
            <Badge variant="secondary" className={`text-xs ${campaign.statut === 'active' ? 'bg-[#ECFDF5] text-[#059669]' : 'bg-[#F3F4F6] text-[#6B7280]'}`}>
              {campaign.statut}
            </Badge>
          </div>
          {campaign.description && <p className="text-sm text-[#71717A] mt-0.5">{campaign.description}</p>}
          {campaign.concurrent && (
            <div className="mt-1.5">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-[#FEF2F2] text-[#B91C1C] font-medium" data-testid="campaign-concurrent-badge">
                ⚔️ Prestataire cible : {campaign.concurrent}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: 'Prospects', value: campaign.nb_prospects, icon: Users, color: '#0055FF' },
          { label: 'Enrichis', value: campaign.nb_enrichis, icon: Phone, color: '#10B981' },
          { label: 'RDV', value: campaign.nb_rdv, icon: Target, color: '#F97316' },
          { label: 'Convertis', value: campaign.nb_convertis, icon: Target, color: '#8B5CF6' },
          { label: 'Relances', value: campaign.nb_relances, icon: Calendar, color: '#F59E0B' },
        ].map((kpi, i) => (
          <Card key={i} className="border border-[#E5E7EB] shadow-sm">
            <CardContent className="p-3 flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${kpi.color}10` }}>
                <kpi.icon className="w-4 h-4" style={{ color: kpi.color }} />
              </div>
              <div>
                <p className="text-lg font-bold" style={{ fontFamily: 'var(--font-heading)' }}>{kpi.value}</p>
                <p className="text-xs text-[#71717A]">{kpi.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Upload + Enrichment */}
      <div className="flex gap-3 flex-wrap">
        <div {...getRootProps()}
          className={`upload-zone rounded-lg px-6 py-3 flex items-center gap-3 cursor-pointer text-sm ${isDragActive ? 'active' : ''}`}
          data-testid="campaign-upload-zone">
          <input {...getInputProps()} />
          <Upload className="w-4 h-4 text-[#0055FF]" />
          {uploading ? 'Upload...' : isDragActive ? 'Déposez ici' : 'Importer un fichier Excel'}
        </div>
        {imports.map(imp => (
          imp.statut === 'en_cours' ? (
            <div key={imp.id} className="flex items-center gap-2 px-4 py-2 bg-[#F9FAFB] rounded-lg border border-[#E5E7EB] text-sm">
              <RefreshCw className="w-3.5 h-3.5 text-[#0055FF] animate-spin" />
              <span>{imp.nb_traites || 0}/{imp.nb_total_enrichissement || imp.nb_lignes}</span>
              <Progress value={((imp.nb_traites || 0) / (imp.nb_total_enrichissement || imp.nb_lignes || 1)) * 100} className="h-1.5 w-24" />
            </div>
          ) : (imp.statut === 'importe' || imp.statut === 'termine') ? (
            <React.Fragment key={imp.id}>
              <Button variant="outline" size="sm" onClick={() => startEnrichment(imp.id, imp.statut === 'termine')}
                className="h-9 gap-1.5 border-[#F97316] text-[#F97316]" data-testid={`enrich-campaign-${imp.id}`}>
                <Zap className="w-3.5 h-3.5" />{imp.statut === 'termine' ? 'Ré-enrichir' : 'Enrichir'}
              </Button>
              {imp.statut === 'termine' && (
                <Button variant="outline" size="sm" onClick={() => startDirigeantEnrichment(imp.id)}
                  className="h-9 gap-1.5 border-[#8B5CF6] text-[#8B5CF6]" data-testid={`dirigeants-campaign-${imp.id}`}>
                  <Users className="w-3.5 h-3.5" />Dirigeants
                </Button>
              )}
            </React.Fragment>
          ) : null
        ))}
        <Button variant="outline" size="sm" onClick={() => setShowExport(true)} className="h-9 gap-1.5 border-[#E5E7EB]" data-testid="campaign-export-btn">
          <Download className="w-3.5 h-3.5" /> Exporter
        </Button>
      </div>

      {/* Search + Filters */}
      <div className="space-y-3">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#71717A]" />
            <Input placeholder="Rechercher..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
              className="pl-10 h-9 bg-white border-[#E5E7EB]" data-testid="campaign-search" />
          </div>
          <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)} className="h-9 gap-1.5">
            <Filter className="w-3.5 h-3.5" /> Filtres
          </Button>
        </div>
        {showFilters && (
          <div className="flex flex-wrap gap-3 p-3 bg-[#F9FAFB] rounded-lg border border-[#E5E7EB]">
            <Select value={filters.statut_commercial} onValueChange={v => { setFilters(f => ({ ...f, statut_commercial: v === 'all' ? '' : v })); setPage(1); }}>
              <SelectTrigger className="w-44 h-8 bg-white text-xs"><SelectValue placeholder="Statut commercial" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                {Object.entries(COMMERCIAL_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.statut_recherche} onValueChange={v => { setFilters(f => ({ ...f, statut_recherche: v === 'all' ? '' : v })); setPage(1); }}>
              <SelectTrigger className="w-44 h-8 bg-white text-xs"><SelectValue placeholder="Statut recherche" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                {Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 gap-1.5 bg-white text-xs" data-testid="campaign-filter-enseigne-btn">
                  <Store className="w-3 h-3 text-[#71717A]" />
                  {filters.enseigne.length === 0 ? 'Enseigne' : `${filters.enseigne.length} enseigne${filters.enseigne.length > 1 ? 's' : ''}`}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-72 p-2" align="start">
                <div className="flex items-center justify-between mb-2 px-1">
                  <span className="text-xs font-semibold">Enseigne</span>
                  {filters.enseigne.length > 0 && (
                    <button onClick={() => { setFilters(f => ({ ...f, enseigne: [] })); setPage(1); }} className="text-xs text-[#F97316] hover:underline">Effacer</button>
                  )}
                </div>
                <ScrollArea className="h-60">
                  <div className="space-y-0.5">
                    {(facets.enseignes || []).length === 0 && <p className="text-xs text-[#71717A] p-2">Aucune donnée</p>}
                    {(facets.enseignes || []).map(e => (
                      <button key={e.value} onClick={() => toggleEnseigne(e.value)}
                        className={`w-full text-left flex items-center justify-between px-2 py-1.5 rounded hover:bg-[#F4F4F5] text-xs ${filters.enseigne.includes(e.value) ? 'bg-[#FFF7ED]' : ''}`}>
                        <span className="flex items-center gap-2">
                          <span className={`w-4 h-4 rounded border flex items-center justify-center ${filters.enseigne.includes(e.value) ? 'bg-[#F97316] border-[#F97316]' : 'border-[#D1D5DB]'}`}>
                            {filters.enseigne.includes(e.value) && <Check className="w-3 h-3 text-white" />}
                          </span>
                          <span className="truncate">{e.value}</span>
                        </span>
                        <span className="text-[10px] text-[#71717A]">{e.count}</span>
                      </button>
                    ))}
                  </div>
                </ScrollArea>
              </PopoverContent>
            </Popover>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 gap-1.5 bg-white text-xs" data-testid="campaign-filter-dept-btn">
                  <MapPin className="w-3 h-3 text-[#71717A]" />
                  {filters.departement.length === 0 ? 'Département' : `${filters.departement.length} dépt`}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-72 p-2" align="start">
                <div className="flex items-center justify-between mb-2 px-1">
                  <span className="text-xs font-semibold">Département</span>
                  {filters.departement.length > 0 && (
                    <button onClick={() => { setFilters(f => ({ ...f, departement: [] })); setPage(1); }} className="text-xs text-[#F97316] hover:underline">Effacer</button>
                  )}
                </div>
                <ScrollArea className="h-60">
                  <div className="grid grid-cols-3 gap-1">
                    {(facets.departements || []).length === 0 && <p className="text-xs text-[#71717A] p-2 col-span-3">Aucune donnée</p>}
                    {(facets.departements || []).map(d => (
                      <button key={d.value} onClick={() => toggleDept(d.value)}
                        className={`flex items-center justify-between px-2 py-1.5 rounded hover:bg-[#F4F4F5] text-xs ${filters.departement.includes(d.value) ? 'bg-[#FFF7ED] border border-[#F97316]' : 'border border-transparent'}`}>
                        <span className="font-mono font-semibold">{d.value}</span>
                        <span className="text-[#71717A]">{d.count}</span>
                      </button>
                    ))}
                  </div>
                </ScrollArea>
              </PopoverContent>
            </Popover>
            {(search || filters.statut_recherche || filters.statut_commercial || filters.enseigne.length > 0 || filters.departement.length > 0) && (
              <Button variant="ghost" size="sm" onClick={() => { setFilters({ statut_recherche: '', statut_commercial: '', enseigne: [], departement: [] }); setSearch(''); setPage(1); }} className="h-8 text-xs gap-1 text-[#71717A]">
                <X className="w-3 h-3" /> Reset
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Prospects Table */}
      <div className="bg-white rounded-lg border border-[#E5E7EB] shadow-sm overflow-x-auto">
        <table className="w-full text-sm" data-testid="campaign-prospects-table">
          <thead>
            <tr className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
              {['Enseigne', 'Adresse', 'CP', 'Ville', 'Téléphone', 'Dirigeant', 'Email', 'CA Aliment.', 'Commercial', 'Concurrent', 'Score', 'Statut', 'Progression', 'Actions'].map(h => (
                <th key={h} className="text-left py-2.5 px-3 text-[#71717A] font-medium text-xs uppercase tracking-wider whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i} className="border-b border-[#E5E7EB]/50">
                  {Array.from({ length: 14 }).map((_, j) => <td key={j} className="py-2.5 px-3"><div className="h-4 bg-[#F3F4F6] rounded animate-pulse" /></td>)}
                </tr>
              ))
            ) : prospects.length === 0 ? (
              <tr><td colSpan={14} className="py-12 text-center text-[#71717A]">Aucun prospect dans cette campagne. Importez un fichier Excel.</td></tr>
            ) : prospects.map(p => (
              <tr key={p.id} className="prospect-row border-b border-[#E5E7EB]/50 cursor-pointer"
                onClick={() => setSelectedProspect(p)} data-testid={`campaign-prospect-${p.id}`}>
                <td className="py-2.5 px-3 font-medium text-xs">{p.enseigne}</td>
                <td className="py-2.5 px-3 text-xs text-[#71717A] max-w-[200px] truncate" title={p.rue}>{p.rue || '-'}</td>
                <td className="py-2.5 px-3 text-xs text-[#71717A]">{p.code_postal}</td>
                <td className="py-2.5 px-3 text-xs">{p.ville}</td>
                <td className="py-2.5 px-3">
                  {p.telephone !== '?' ? (
                    <a href={`tel:${p.telephone?.replace(/\s/g, '')}`} className="font-mono text-xs font-medium hover:text-[#0055FF] underline-offset-2 hover:underline"
                      onClick={e => e.stopPropagation()}>{p.telephone}</a>
                  ) : <span className="text-xs text-[#71717A]">?</span>}
                </td>
                <td className="py-2.5 px-3 text-xs">
                  {(p.prenom_dirigeant || p.nom_dirigeant) ? (
                    <span className="text-[#8B5CF6] font-medium">{p.prenom_dirigeant} {p.nom_dirigeant}</span>
                  ) : <span className="text-[#71717A]">-</span>}
                </td>
                <td className="py-2.5 px-3 text-xs max-w-[220px] truncate">
                  {p.email_professionnel ? (
                    <a href={`mailto:${p.email_professionnel}`} className="text-[#0055FF] hover:underline"
                      onClick={e => e.stopPropagation()} title={p.email_professionnel}>{p.email_professionnel}</a>
                  ) : <span className="text-[#A1A1AA] italic">-</span>}
                </td>
                <td className="py-2.5 px-3 text-xs whitespace-nowrap">
                  {p.ca_alimentaire_estimatif ? (
                    <span className="font-mono font-semibold text-[#15803D]">{formatCA(p.ca_alimentaire_estimatif)}</span>
                  ) : <span className="text-[#A1A1AA] italic">-</span>}
                </td>
                <td className="py-2.5 px-3 text-xs whitespace-nowrap">
                  {p.commercial_assigne_nom ? (
                    <span className="inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded text-[11px] font-medium"
                      style={{ background: (p.commercial_assigne_color || '#71717A') + '22', color: p.commercial_assigne_color || '#27272A' }}>
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: p.commercial_assigne_color || '#71717A' }}></span>
                      {p.commercial_assigne_nom}
                    </span>
                  ) : <span className="text-[#A1A1AA] italic">non assigné</span>}
                </td>
                <td className="py-2.5 px-3 text-xs">
                  {campaign.concurrent ? (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] bg-[#FEF2F2] text-[#B91C1C] font-medium">
                      ⚔️ {campaign.concurrent}
                    </span>
                  ) : <span className="text-[#71717A]">-</span>}
                </td>
                <td className="py-2.5 px-3">
                  {p.score_confiance > 0 && (
                    <span className={`inline-flex px-1.5 py-0.5 rounded text-xs font-medium ${p.score_confiance >= 70 ? 'score-high' : p.score_confiance >= 50 ? 'score-medium' : 'score-low'}`}>
                      {p.score_confiance}%
                    </span>
                  )}
                </td>
                <td className="py-2.5 px-3">
                  <Badge variant="secondary" className={`text-xs status-${p.statut_recherche}`}>
                    {STATUS_LABELS[p.statut_recherche] || p.statut_recherche}
                  </Badge>
                </td>
                <td className="py-2.5 px-3">
                  <Badge variant="secondary" className={`text-xs commercial-${p.statut_commercial}`}>
                    {COMMERCIAL_LABELS[p.statut_commercial] || p.statut_commercial}
                  </Badge>
                </td>
                <td className="py-2.5 px-3" onClick={e => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0"><MoreHorizontal className="w-3.5 h-3.5" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-44">
                      <DropdownMenuItem onClick={() => setSelectedProspect(p)}><Phone className="w-3.5 h-3.5 mr-2" /> Voir fiche</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => reSearchProspect(p.id)} data-testid={`campaign-research-${p.id}`}>
                        <RefreshCw className="w-3.5 h-3.5 mr-2" /> Re-rechercher
                      </DropdownMenuItem>
                      {Object.entries(COMMERCIAL_LABELS).map(([k, v]) => (
                        <DropdownMenuItem key={k} onClick={() => quickCallResult(p.id, k)} className="text-xs">{v}</DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-[#71717A]">Page {page}/{totalPages} ({total})</p>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="h-7 w-7 p-0"><ChevronLeft className="w-3.5 h-3.5" /></Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="h-7 w-7 p-0"><ChevronRight className="w-3.5 h-3.5" /></Button>
          </div>
        </div>
      )}

      {selectedProspect && <ProspectDrawer prospect={selectedProspect} onClose={() => setSelectedProspect(null)} onUpdate={() => { loadProspects(); loadCampaign(); }} commerciaux={commerciaux} />}
      {showExport && <ExportModal onClose={() => setShowExport(false)} commerciaux={commerciaux} />}
    </div>
  );
}
